import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../apiConfig';

const CATEGORY = 'roots';
const STORAGE_KEY = `crops_${CATEGORY}_data_public`;

const ROOT_CROPS = [
    { value: 'cassava', label: 'මඤ්ඤොක්කා (Cassava)' },
    { value: 'sweet_potato', label: 'බතල (Sweet Potato)' },
    { value: 'innala', label: 'ඉන්නල (Innala)' },
    { value: 'vinetubers', label: 'වැල් අල (vinetubers)' }
];

export default function RootsManage({ onSubmitting }) {
    const [cropsData, setCropsData] = useState(() => {
        const local = localStorage.getItem(STORAGE_KEY);
        try {
            return local ? JSON.parse(local) : [];
        } catch (e) {
            return [];
        }
    });

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        year: '',
        crop: '',
        area: '',
        production: ''
    });
    const [message, setMessage] = useState('');

    useEffect(() => {
        const fetchCropsData = async () => {
            setLoading(true);
            try {
                const res = await fetch(getApiUrl(`api_crops.php?category=${CATEGORY}&t=${Date.now()}`));
                if (!res.ok) throw new Error('Server error');
                const data = await res.json();
                if (Array.isArray(data)) {
                    const sorted = [...data].sort((a, b) => 
                        String(a.year).localeCompare(String(b.year), undefined, { numeric: true })
                    );
                    setCropsData(sorted);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(sorted));
                }
            } catch (err) {
                console.warn('Fallback to local storage');
            } finally {
                setLoading(false);
            }
        };
        fetchCropsData();
    }, []);

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        console.log('📝 [ROOTS] Smart-Sync Submit Started');
        
        if (onSubmitting) onSubmitting(true);
        setMessage('⏳ Syncing...');

        try {
            // 1. Fetch latest server data FIRST
            const res = await fetch(getApiUrl(`api_crops.php?category=${CATEGORY}&t=${Date.now()}`));
            let serverData = [];
            if (res.ok) {
                const data = await res.json();
                serverData = Array.isArray(data) ? data : [];
            }

            const id = `${form.year}_${form.crop}`;
            const existingIndex = serverData.findIndex(item => `${item.year}_${item.crop}` === id);

            // 2. Merge current form into the latest server list
            let updatedData;
            if (existingIndex !== -1) {
                updatedData = serverData.map((item, index) => 
                    index === existingIndex ? { ...form } : item
                );
            } else {
                updatedData = [...serverData, form].sort((a, b) => 
                    String(a.year).localeCompare(String(b.year), undefined, { numeric: true })
                );
            }

            // 3. Update UI and Local
            setCropsData(updatedData);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));

            // 4. Save COMPLETE combined list back to server
            const saveRes = await fetch(getApiUrl(`api_crops.php?category=${CATEGORY}`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedData)
            });

            if (saveRes.ok) {
                setMessage('✅ දත්ත සාර්ථකව සුරැකිණි!');
                setForm({ year: '', crop: '', area: '', production: '' });
            } else {
                throw new Error('Sync failed');
            }

        } catch (err) {
            console.error('Roots Sync Error:', err);
            setMessage('⚠ සර්වර් දෝෂයකි, කරුණාකර නැවත උත්සාහ කරන්න.');
        } finally {
            if (onSubmitting) onSubmitting(false);
            setTimeout(() => setMessage(''), 4000);
        }
        return;
    };

    const handleDelete = async (itemToRemove) => {
        if (!window.confirm('මෙම දත්ත මැකීමට අවශ්‍ය බව සැකද?')) return;

        const updatedData = cropsData.filter(item =>
            !(item.year === itemToRemove.year && item.crop === itemToRemove.crop)
        );

        setCropsData(updatedData);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));

        try {
            await fetch(getApiUrl(`api_crops.php?category=${CATEGORY}`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedData)
            });
            setMessage('🗑️ දත්ත සාර්ථකව මකා දමන ලදී!');
        } catch (err) {
            setMessage('⚠ සර්වර් දෝෂයකි.');
        }
        setTimeout(() => setMessage(''), 4000);
    };

    const handleEdit = (item) => {
        setForm({ ...item });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="manager-container">
            <div className="form-wrapper">
                <div className="premium-card">
                    <div className="card-header">
                        <div className="icon-box">🥔</div>
                        <h3 className="section-title">නව දත්ත ඇතුළත් කර සුරකින්න</h3>
                        {loading && <p className="loading-text">සර්වර් එකෙන් දත්ත ලබා ගනිමින් පවතියි...</p>}
                    </div>

                    {message && <div className={`status-message ${message.includes('✅') ? 'success' : 'error'}`}>{message}</div>}

                    <form className="premium-form" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); console.log('🛑 Form submission attempt blocked'); return false; }}>
                        <div className="form-group">
                            <label>කන්නය</label>
                            <input
                                type="text"
                                value={form.year}
                                onChange={e => setForm({ ...form, year: e.target.value })}
                                placeholder="උදා: 2023/2024 මහා හෝ 2024 යල"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>බෝගය</label>
                            <select
                                value={form.crop}
                                onChange={e => setForm({ ...form, crop: e.target.value })}
                                required
                                className="premium-select"
                            >
                                <option value="">බෝගය තෝරන්න...</option>
                                {ROOT_CROPS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>වගා වපසරිය (හෙක්)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.area}
                                onChange={e => setForm({ ...form, area: e.target.value })}
                                placeholder="හෙක්ටයාර ප්‍රමාණය"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>වගා නිෂ්පාදනය (මෙ.ටො)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.production}
                                onChange={e => setForm({ ...form, production: e.target.value })}
                                placeholder="මෙට්‍රික් ටොන් ප්‍රමාණය"
                                required
                            />
                        </div>

                        <button type="button" className="submit-btn primary" onClick={handleSubmit}>
                            <span className="btn-icon">💾</span> දත්ත සුරකින්න
                        </button>
                    </form>
                </div>

                <div className="premium-card list-card" style={{ marginTop: '30px' }}>
                    <div className="card-header">
                        <h3 className="section-title">පවතින අල බෝග දත්ත</h3>
                    </div>
                    <div className="table-wrapper">
                        <table className="manage-table">
                            <thead>
                                <tr>
                                    <th>කන්නය</th>
                                    <th>බෝගය</th>
                                    <th>වපසරිය(හෙක්)</th>
                                    <th>නිෂ්පාදනය(මෙ.ටො)</th>
                                    <th>ක්‍රියා</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cropsData.length === 0 ? (
                                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>දත්ත නොමැත</td></tr>
                                ) : (
                                    cropsData.map((item, idx) => (
                                        <tr key={idx}>
                                            <td data-label="වර්ශය"><strong>{item.year}</strong></td>
                                            <td data-label="බෝගය">{ROOT_CROPS.find(c => c.value === item.crop)?.label || item.crop}</td>
                                            <td data-label="වපසරිය">{item.area}</td>
                                            <td data-label="නිෂ්පාදනය">{item.production}</td>
                                            <td data-label="ක්‍රියා">
                                                <div className="action-cell">
                                                    <button onClick={() => handleEdit(item)} className="icon-btn edit-btn">📝</button>
                                                    <button onClick={() => handleDelete(item)} className="icon-btn delete-btn">🗑️</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <style>{`
                .premium-select {
                    width: 100%;
                    padding: 16px 20px;
                    border: 2px solid #f3f4f6;
                    border-radius: 16px;
                    font-size: 1.05rem;
                    background: #f9fafb;
                    cursor: pointer;
                }
                .premium-select:focus {
                    outline: none;
                    border-color: #10b981;
                    background: #fff;
                }
                .manager-container { padding: 20px; display: flex; justify-content: center; font-family: 'Inter', sans-serif; }
                .form-wrapper { width: 100%; max-width: 600px; }
                .premium-card { background: #fff; border-radius: 24px; padding: clamp(20px, 5vw, 40px); box-shadow: 0 10px 30px rgba(0,0,0,0.05); }
                .icon-box {
                    width: 70px;
                    height: 70px;
                    background: #f0f9ff;
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2.2rem;
                    margin: 0 auto 15px auto;
                    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.02);
                }
                .loading-text {
                    font-size: 0.85rem;
                    color: #64748b;
                    margin-top: 8px;
                    font-weight: 500;
                }
                .section-title { color: #1a3353; font-weight: 700; font-size: 1.25rem; margin: 0; }
                .status-message { padding: 15px; border-radius: 12px; margin-bottom: 20px; text-align: center; font-weight: 600; }
                .status-message.success { background: #ecfdf5; color: #059669; }
                .status-message.error { background: #fef2f2; color: #dc2626; }
                .form-group { margin-bottom: 20px; }
                .form-group label { display: block; margin-bottom: 8px; font-weight: 600; color: #4b5563; }
                .premium-form input { width: 100%; padding: 16px; border: 2px solid #f3f4f6; border-radius: 12px; box-sizing: border-box; }
                .submit-btn { width: 100%; padding: 16px; border-radius: 12px; border: none; font-weight: 700; cursor: pointer; transition: 0.3s; }
                .submit-btn.primary { background: #10b981; color: #fff; }
                .submit-btn.primary:hover { background: #059669; transform: translateY(-2px); }
                .manage-table { width: 100%; border-collapse: collapse; }
                .manage-table th { text-align: left; padding: 12px; background: #f8fafc; border-bottom: 2px solid #edf2f7; }
                .manage-table td { padding: 12px; border-bottom: 1px solid #edf2f7; }
                .action-cell { display: flex; gap: 8px; }
                .icon-btn { background: none; border: none; cursor: pointer; font-size: 1.2rem; }
                @media (max-width: 600px) {
                    .manage-table thead { display: none; }
                    .manage-table tr { display: block; border: 1px solid #eee; margin-bottom: 10px; padding: 10px; border-radius: 12px; }
                    .manage-table td { display: flex; justify-content: space-between; border: none; padding: 5px 0; }
                    .manage-table td::before { content: attr(data-label); font-weight: 700; color: #666; }
                }
            `}</style>
        </div>
    );
}
